import { tac } from '@eulerxyz/core-sdk'
import {
  type Address,
  type CallParameters,
  type Client,
  type Hex,
  type Transport,
  formatTransactionRequest,
  zeroAddress,
  zeroHash,
} from 'viem'
import type { Account } from 'viem/accounts'
import type { Chain } from 'viem/chains'
import { serializeStateOverride } from '../utils/stateOverride'

// Local type definitions to avoid viem internal imports
type BlockTag = 'latest' | 'earliest' | 'pending' | 'safe' | 'finalized'
type BlockNumber = `0x${string}`
type BlockIdentifier = { blockHash: Hex } | { blockNumber: BlockNumber }
type TransactionRequest = {
  from?: Address
  to?: Address
  gas?: Hex
  gasPrice?: Hex
  maxFeePerGas?: Hex
  maxPriorityFeePerGas?: Hex
  value?: Hex
  data?: Hex
  nonce?: Hex
}
type RpcStateOverride = Record<
  Address,
  {
    balance?: Hex
    nonce?: Hex
    code?: Hex
    state?: Record<Hex, Hex>
    stateDiff?: Record<Hex, Hex>
  }
>
type ExactPartial<T> = { [K in keyof T]?: T[K] }
type UnionOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never

type TraceCallParameters = UnionOmit<CallParameters, 'account'> & {
  from: Address
}

type Storage = {
  [key in string]: {
    storage: {
      [key in Hex]: Hex
    }
    balance: Hex
  }
}

type DiffModePrestateTracer = {
  post: Storage
  pre: Storage
}

export type CallStateTracer = {
  calls?: CallStateTracer[]
  output?: Hex
  input?: Hex
  error?: string
  to: Address
  from: Address
}

type PrestateTracerSchema = {
  Method: 'debug_traceCall'
  Parameters: [
    transaction: ExactPartial<TransactionRequest>,
    block: BlockNumber | BlockTag | BlockIdentifier,
    tracer: {
      tracer: 'prestateTracer'
      tracerConfig: { diffMode: true }
      stateOverrides?: RpcStateOverride | undefined
    },
  ]
  ReturnType: DiffModePrestateTracer
}

type DefaultTracerSchema = {
  Method: 'debug_traceCall'
  Parameters: [
    transaction: ExactPartial<TransactionRequest>,
    block: BlockNumber | BlockTag | BlockIdentifier,
    tracer: {
      tracer: 'prestateTracer'
      tracerConfig: {
        enableMemory: true

        disableStack: false
        disableStorage: false
        enableReturnData: true
      }
      stateOverrides?: RpcStateOverride | undefined
    },
  ]
  ReturnType: Storage
}

type CallTracerSchema = {
  Method: 'debug_traceCall'
  Parameters: [
    transaction: ExactPartial<TransactionRequest>,
    block: BlockNumber | BlockTag | BlockIdentifier,
    tracer: {
      tracer: 'callTracer'
      stateOverrides?: RpcStateOverride | undefined
    },
  ]
  ReturnType: CallStateTracer
}

export type DebugActions = {
  prestateTracer(args: TraceCallParameters): Promise<DiffModePrestateTracer>
  defaultTracer(args: TraceCallParameters): Promise<Storage>
  callTracer(args: TraceCallParameters): Promise<CallStateTracer>
}

export function debugActions<
  transport extends Transport = Transport,
  chain extends Chain | undefined = Chain | undefined,
  account extends Account | undefined = Account | undefined,
>(client: Client<transport, chain, account>): DebugActions {
  if (client.chain?.id === tac.id) {
    console.warn('TAC chain detected, using mock debug_traceCall actions')
    return {
      prestateTracer: async () => ({ post: {}, pre: {} }),
      defaultTracer: async () => ({}),
      callTracer: async () => ({
        to: zeroAddress,
        from: zeroAddress,
        calls: [],
        output: zeroHash,
        input: zeroHash,
      }),
    }
  }
  return {
    async prestateTracer({ stateOverride, ...args }: TraceCallParameters): Promise<DiffModePrestateTracer> {
      const stateOverrides = serializeStateOverride(stateOverride)
      // @ts-ignore - Type mismatch with viem RPC schema
      return client.request<PrestateTracerSchema>({
        method: 'debug_traceCall',
        params: [
          // @ts-ignore - Type mismatch with TransactionRequest
          formatTransactionRequest(args),
          'latest',
          {
            tracer: 'prestateTracer',
            stateOverrides,
            tracerConfig: { diffMode: true },
          },
        ],
      })
    },
    async defaultTracer({ stateOverride, ...args }: TraceCallParameters): Promise<Storage> {
      const stateOverrides = serializeStateOverride(stateOverride)
      // @ts-ignore - Type mismatch with viem RPC schema
      return client.request<DefaultTracerSchema>({
        method: 'debug_traceCall',
        params: [
          // @ts-ignore - Type mismatch with TransactionRequest
          formatTransactionRequest(args),
          'latest',
          {
            tracer: 'prestateTracer',
            stateOverrides,
            tracerConfig: {
              enableMemory: true,
              disableStack: false,
              disableStorage: false,
              enableReturnData: true,
            },
          },
        ],
      })
    },
    async callTracer({ stateOverride, ...args }: TraceCallParameters): Promise<CallStateTracer> {
      const stateOverrides = serializeStateOverride(stateOverride)
      // @ts-ignore - Type mismatch with viem RPC schema
      return client.request<CallTracerSchema>({
        method: 'debug_traceCall',
        params: [
          // @ts-ignore - Type mismatch with TransactionRequest
          formatTransactionRequest(args),
          'latest',
          {
            tracer: 'callTracer',
            stateOverrides,
          },
        ],
      })
    },
  }
}
